import React, { useEffect, useMemo, useState } from 'react'
import { Avatar, Divider, Link, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { NewsArticle } from '../../proto/generated/market_news'
import { LOGOS_BASE_URL } from '../../assets/constants'
import { iconSelectionColor } from '../../assets/colors'
import { Security } from '../../proto/generated/security.ts'
import { handleOverviewSecurityClick } from '../securityInfo/SecurityOverviewHeader.tsx'
import { formatUnixTimestampReadable } from '../../util/dateTime.ts'
import { getColorForSymbol } from '../../util/general.ts'
import { exchangeFromJSON } from '../../proto/generated/common'
import { getSecurityAsync, getSecurityKey } from '../../util/security'

interface Props {
    newsArticle: NewsArticle
}

const PROVIDER_NAMES: Record<string, string> = {
    'CNBC': 'CNBC',
    'ECONOMIC_TIMES': 'Economic Times',
    'MONEY_CONTROL': 'Money Control',
    'NDTV_PROFIT': 'NDTV Profit',
    'NSE': 'NSE',
    'BSE': 'BSE',
    'PROVIDER_UNKNOWN': 'Unknown'
}

const useStyles = makeStyles()((theme) => ({
    title: {
        cursor: 'pointer',
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitLineClamp: 1,
        lineClamp: 1,
        WebkitBoxOrient: 'vertical',
        '&:hover': {
            color: iconSelectionColor
        }
    },
    summary: {
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitLineClamp: 2,
        lineClamp: 2,
        WebkitBoxOrient: 'vertical',
        color: theme.palette.text.secondary
    },
    providerText: {
        color: theme.palette.text.secondary
    },
    rootContainer: {
        padding: theme.spacing(1, 2),
        width: '100%'
    }
}))

const NewsWidget = React.memo(({ newsArticle }: Props) => {
    const { classes } = useStyles()
    const theme = useTheme()

    const [securities, setSecurities] = useState<Security[]>([])
    useEffect(() => {
        if (!newsArticle.primaryCompanyIdentifier?.length) {
            setSecurities([])
            return
        }
        let cancelled = false
        Promise.all(newsArticle.primaryCompanyIdentifier.map(async (identifier) => {
            const exchange = exchangeFromJSON(identifier.primaryExchange)
            const securityKey = getSecurityKey(exchange, identifier.primaryCode)
            const security = await getSecurityAsync(securityKey)
            if (security) return security
            return { code: identifier.primaryCode, displayName: identifier.primaryCode, exchange, companyCode: identifier.primaryCode } as Security
        })).then((secs) => {
            if (!cancelled) setSecurities(secs.filter(Boolean))
        })
        return () => { cancelled = true }
    }, [newsArticle.primaryCompanyIdentifier])

    const providerName = useMemo(() => {
        return PROVIDER_NAMES[newsArticle.providerCode] || newsArticle.providerCode
    }, [newsArticle.providerCode])

    const timeAgoText = useMemo(() => formatUnixTimestampReadable(newsArticle.created), [newsArticle.created])

    return (
        <Stack>
            <Stack className={classes.rootContainer} spacing={0.5}>
                <Stack direction="row" alignItems="center">
                    <Link href={newsArticle.link} target="_blank" underline="none" color="inherit">
                        <Typography variant={'body1'} className={classes.title}>
                            {newsArticle.title}
                        </Typography>
                    </Link>
                </Stack>
                <Typography className={classes.summary} variant="body2">
                    {newsArticle.summary}
                </Typography>
                <Stack alignItems="center" direction="row" spacing={securities.length > 0 ? 1.5 : 0}>
                    {securities.length > 0 && (
                        <Stack direction="row" alignItems="center" sx={{ position: 'relative', display: 'flex' }}>
                            {securities.map((security, i) => (
                                <Tooltip key={i} title={security.displayName || security.code} arrow>
                                    {security.logoUrl ? (
                                        <Avatar
                                            alt={security.code}
                                            src={`${LOGOS_BASE_URL}${security.logoUrl}.svg`}
                                            onClick={() => handleOverviewSecurityClick(security)}
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                cursor: 'pointer',
                                                border: '1px solid white',
                                                position: 'relative',
                                                zIndex: securities.length - i,
                                                marginLeft: i === 0 ? 0 : '-6px',
                                                transition: 'transform 0.2s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.15)',
                                                    zIndex: securities.length + 1
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Avatar
                                            alt={security.code}
                                            onClick={() => handleOverviewSecurityClick(security)}
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                cursor: 'pointer',
                                                border: '1px solid white',
                                                position: 'relative',
                                                zIndex: securities.length - i,
                                                marginLeft: i === 0 ? 0 : '-6px',
                                                transition: 'transform 0.2s ease',
                                                backgroundColor: getColorForSymbol(security.code, theme.palette.background.default),
                                                fontSize: 10,
                                                fontWeight: 600,
                                                '&:hover': {
                                                    transform: 'scale(1.15)',
                                                    zIndex: securities.length + 1
                                                }
                                            }}
                                        >
                                            {security.code
                                                .split(' ')
                                                .map((word) => word[0]?.toUpperCase())
                                                .slice(0, 2)
                                                .join('')}
                                        </Avatar>
                                    )}
                                </Tooltip>
                            ))}
                        </Stack>
                    )}
                    <Typography variant="caption" className={classes.providerText}>
                        {`${timeAgoText} - ${providerName}`}
                    </Typography>
                </Stack>
            </Stack>
            <Divider />
        </Stack>
    )
})

export default NewsWidget
